import React from 'react';
import { ComparisonWidget, GroupByDimension } from '../../services/comparisonDashboardService';
import {
  COMPARISON_GROUP_BY_OPTIONS,
  COMPARISON_METRIC_COLUMNS,
  COMPARISON_SECONDARY_LABEL,
  formatComparisonMetricValue,
} from './comparisonBreakdownConstants';

const styles: Record<string, React.CSSProperties> = {
  groupByRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0 0 0.75rem',
    flexWrap: 'wrap' as const,
  },
  groupByLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'var(--report-text)',
  },
  groupByButtons: {
    display: 'flex',
    gap: '0.35rem',
    flexWrap: 'wrap' as const,
  },
  groupByBtn: {
    padding: '0.4rem 0.85rem',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    background: 'var(--report-surface)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--report-text)',
    transition: 'all 0.15s',
  },
  groupByBtnActive: {
    background: 'var(--report-accent)',
    color: 'white',
    borderColor: 'var(--report-accent)',
  },
  loadingBox: {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--report-text-muted)',
    fontSize: '0.95rem',
  },
  widgetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))',
    gap: '1rem',
    padding: '0 0 1rem',
  },
  widgetCard: {
    background: 'var(--report-surface)',
    border: '1px solid var(--report-border)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  widgetHeader: {
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '1.05rem',
    color: 'var(--report-text)',
    borderBottom: '1px solid var(--report-border)',
    background: 'var(--report-surface-subtle)',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    padding: '0.5rem 0.65rem',
    textAlign: 'right' as const,
    fontWeight: 600,
    color: 'var(--report-text-muted)',
    borderBottom: '2px solid var(--report-border)',
    whiteSpace: 'nowrap' as const,
    fontSize: '0.8rem',
  },
  thFirst: {
    textAlign: 'left' as const,
  },
  td: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid var(--report-border)',
    color: 'var(--report-text)',
  },
  tdNum: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid var(--report-border)',
    color: 'var(--report-text)',
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  },
};

export const ComparisonWidgetCard: React.FC<{
  widget: ComparisonWidget;
  secondaryLabel: string;
}> = ({ widget, secondaryLabel }) => (
  <div style={styles.widgetCard}>
    <div style={styles.widgetHeader}>{widget.group_label}</div>
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, ...styles.thFirst }}>{secondaryLabel}</th>
            {COMPARISON_METRIC_COLUMNS.map((col) => (
              <th key={col.key} style={styles.th}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {widget.rows.map((row) => (
            <tr key={row.row_key}>
              <td style={{ ...styles.td, fontWeight: 500 }}>{row.row_label}</td>
              {COMPARISON_METRIC_COLUMNS.map((col) => (
                <td key={col.key} style={styles.tdNum}>
                  {formatComparisonMetricValue(
                    (row as unknown as Record<string, number>)[col.key],
                    col.format
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export interface ComparisonBreakdownGridProps {
  groupBy: GroupByDimension;
  onGroupByChange: (gb: GroupByDimension) => void;
  widgets: ComparisonWidget[];
  loading: boolean;
  emptyMessage?: string;
}

/**
 * Group-by toolbar and comparison widget cards (same layout as Comparison Dashboard).
 */
const ComparisonBreakdownGrid: React.FC<ComparisonBreakdownGridProps> = ({
  groupBy,
  onGroupByChange,
  widgets,
  loading,
  emptyMessage = 'No data found for the selected filters.',
}) => (
  <>
    <div style={styles.groupByRow}>
      <span style={styles.groupByLabel}>Group by</span>
      <div style={styles.groupByButtons}>
        {COMPARISON_GROUP_BY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onGroupByChange(opt.value)}
            style={{
              ...styles.groupByBtn,
              ...(groupBy === opt.value ? styles.groupByBtnActive : {}),
            }}
            disabled={loading}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>

    {loading ? (
      <div style={styles.loadingBox}>Loading comparison data...</div>
    ) : widgets.length === 0 ? (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--report-text-muted)' }}>
        {emptyMessage}
      </div>
    ) : (
      <div style={styles.widgetGrid}>
        {widgets.map((w) => (
          <ComparisonWidgetCard
            key={w.group_key}
            widget={w}
            secondaryLabel={COMPARISON_SECONDARY_LABEL[groupBy]}
          />
        ))}
      </div>
    )}
  </>
);

export default ComparisonBreakdownGrid;
